import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { t } from 'i18next';

import { LeafRowCellRenderer, ParentRowCellRenderer, StatusCell } from 'core/elements';
import { AgGridFilterOption } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import { formatDate, getLabel, isPromotedSingleChild } from 'core/utils';
import { ICorporatePart } from 'corporate/types';
import { statusColorMap } from 'pricing/constants';
import { PricingHelper } from 'pricing/helpers';

import { ActionsCell } from './cells';
import { IFlatCostRow } from './utils';

interface IColumnDefParams {
  corporatePart: Partial<ICorporatePart>;
  canManagePartCosts: boolean;
}

type FlatField = keyof IFlatCostRow;

const parentFieldGetter =
  (field: FlatField) =>
  ({ data, node }: ValueGetterParams<IFlatCostRow>) => {
    if (node?.group) {
      return node.allLeafChildren?.[0]?.data?.[field] ?? null;
    }
    if (isPromotedSingleChild({ node })) {
      return data?.[field] ?? null;
    }

    return null;
  };

const leafFieldGetter =
  (field: FlatField) =>
  ({ data, node }: ValueGetterParams<IFlatCostRow>) => {
    if (node?.group) {
      return null;
    }

    return data?.[field] ?? null;
  };

const leafFilterValueGetter =
  (field: FlatField) =>
  ({ data }: ValueGetterParams<IFlatCostRow>) =>
    data?.[field] ?? null;

const userValueGetter = ({ data, node }: ValueGetterParams<IFlatCostRow>) => {
  const target = node?.group ? node.allLeafChildren?.[0]?.data : data;

  if (!target) {
    return null;
  }
  if (!node?.group && !isPromotedSingleChild({ node })) {
    return null;
  }

  return [target.userFirstName, target.userLastName].filter(Boolean).join(' ');
};

const userFilterValueGetter = ({ data }: ValueGetterParams<IFlatCostRow>) => {
  if (!data) {
    return null;
  }

  return [data.userFirstName, data.userLastName].filter(Boolean).join(' ');
};

export const columnDef = ({ corporatePart, canManagePartCosts }: IColumnDefParams): ColDef[] => {
  const baseColumns: ColDef[] = [
    {
      colId: 'status',
      headerName: t('headerStatus'),
      maxWidth: 150,
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: (params) => getLabel(params.value),
      },
      valueGetter: parentFieldGetter('status'),
      filterValueGetter: leafFilterValueGetter('status'),
      cellRenderer: StatusCell,
      cellRendererParams: { colorMap: statusColorMap },
    },
    {
      colId: 'minQuantity',
      headerName: t('headerMinQty'),
      maxWidth: 150,
      sortable: false,
      enableRowGroup: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      cellRenderer: LeafRowCellRenderer,
      valueGetter: leafFieldGetter('minQuantity'),
      valueFormatter: ({ data, value }) =>
        value != null
          ? NumberHelper.formatNumberPerUom({ amount: Number(value), uom: data?.uom })
          : '',
    },
    {
      colId: 'maxQuantity',
      headerName: t('headerMaxQty'),
      maxWidth: 150,
      sortable: false,
      enableRowGroup: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      cellRenderer: LeafRowCellRenderer,
      valueGetter: leafFieldGetter('maxQuantity'),
      valueFormatter: ({ data, value }) =>
        value != null
          ? NumberHelper.formatNumberPerUom({ amount: Number(value), uom: data?.uom })
          : '',
    },
    {
      colId: 'cost',
      headerName: t('headerCostPerUom'),
      sortable: false,
      enableRowGroup: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      cellRenderer: LeafRowCellRenderer,
      valueGetter: leafFieldGetter('cost'),
      valueFormatter: ({ data, value }) =>
        value != null ? PricingHelper.formatPricingPerUom(Number(value), data?.uom) : '',
    },
    {
      colId: 'startDate',
      headerName: t('headerStartDate'),
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
      cellRenderer: ParentRowCellRenderer,
      valueGetter: parentFieldGetter('startDate'),
      filterValueGetter: leafFilterValueGetter('startDate'),
      valueFormatter: ({ value }) => value && formatDate(value),
    },
    {
      colId: 'endDate',
      headerName: t('headerEndDate'),
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
      cellRenderer: ParentRowCellRenderer,
      valueGetter: parentFieldGetter('endDate'),
      filterValueGetter: leafFilterValueGetter('endDate'),
      valueFormatter: ({ value }) => value && formatDate(value),
    },
    {
      colId: 'updatedBy',
      headerName: t('headerUpdatedBy'),
      filter: 'agTextColumnFilter',
      cellRenderer: ParentRowCellRenderer,
      valueGetter: userValueGetter,
      filterValueGetter: userFilterValueGetter,
    },
    {
      colId: 'createdAt',
      headerName: t('headerUpdatedOn'),
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
      cellRenderer: ParentRowCellRenderer,
      valueGetter: parentFieldGetter('createdAt'),
      filterValueGetter: leafFilterValueGetter('createdAt'),
      valueFormatter: ({ value }) => value && formatDate(value),
    },
  ];

  if (!canManagePartCosts) {
    return baseColumns;
  }

  return [
    {
      colId: 'actions',
      headerName: '',
      field: 'actions',
      width: 80,
      maxWidth: 80,
      resizable: false,
      sortable: false,
      filter: false,
      enableRowGroup: false,
      suppressHeaderMenuButton: true,
      suppressColumnsToolPanel: true,
      pinned: 'left',
      lockPinned: true,
      suppressMovable: true,
      cellRenderer: ActionsCell,
      cellRendererParams: { corporatePart },
    },
    ...baseColumns,
  ];
};
